import { DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import { getGuid } from 'owa-guid';
import updateBookingServiceOperation from 'owa-service/lib/operation/updateBookingServiceOperation';
import { getJsonRequestHeader } from 'owa-service/lib/ServiceRequestUtils';

import getEntryPoint from '../selectors/getEntryPoint';
import getFirstTimeUser from '../selectors/getFirstTimeUser';
import { getUserMailboxGuid } from '../utils/getMailboxGuid';
import getMailboxPuid from '../selectors/getMailboxPuid';

import type UpdateBookingServiceJsonResponse from 'owa-service/lib/contract/UpdateBookingServiceJsonResponse';
import type UpdateBookingServiceResponseMessage from 'owa-service/lib/contract/UpdateBookingServiceResponseMessage';
import type UpdateBookingServiceJsonRequest from 'owa-service/lib/contract/UpdateBookingServiceJsonRequest';
import type BookingMeetingType from 'owa-service/lib/contract/BookingService';
import logReminderFollowUpTelemetry from '360-service-add-edit/lib/utils/logReminderFollowUpTelemetry';
import store from '../store/Store';
import { convertInboxRemindersToGql } from 'convert-calendar-item';
import type { InboxReminder } from 'owa-graph-schema';
/**
 * Service call to update a booking meetingType.
 */
export default async function updateBookingMeetingType(
    meetingType: BookingMeetingType
): Promise<UpdateBookingServiceResponseMessage | null> {
    const request: UpdateBookingServiceJsonRequest = {
        Header: getJsonRequestHeader(),
        Body: {
            Service: meetingType,
        },
    };
    const perfDatapoint = new PerformanceDatapoint('updateBookingMeetingType');
    perfDatapoint.addCustomData({
        meetingTypeId: meetingType.ServiceId,
        entryPoint: getEntryPoint(),
        guid: getGuid(),
        isNewUser: getFirstTimeUser(),
        mailboxGuid: getUserMailboxGuid(),
        bookingMailboxPuid: getMailboxPuid(),
    });

    const inboxRemindersGql: InboxReminder[] = convertInboxRemindersToGql(store.inboxReminders);

    /* eslint-disable-next-line owa-custom-rules/require-getMailboxRequestOptions  -- (https://aka.ms/OWALintWiki)
     * MailboxReqestOptions should be passed into service request calls, see https://aka.ms/multiaccountlinter
     *	> All service requests calls must pass in a MailboxRequestOptions. The can be obtained by passing a MailboxInfo to the getMailboxRequestOptions function. */
    const response: UpdateBookingServiceJsonResponse = await updateBookingServiceOperation(request);

    let result: UpdateBookingServiceResponseMessage | null = null;
    if (response?.Body) {
        result = response.Body;
        if (result.ResponseClass === 'Success') {
            // Corrects the reminderOffset of the inbox reminders in the store before logging telemetry
            const inboxReminders = store.meetingTypeEditState?.inboxReminders ?? [];
            const duration = store.meetingTypeEditState?.duration ?? 0;
            const buffer = store.meetingTypeEditState?.preBuffer ?? 0;
            const correctedInboxReminders: InboxReminder[] = inboxReminders.map(inboxReminder => {
                if (
                    inboxReminder &&
                    inboxReminder.ReminderOffset != undefined &&
                    inboxReminder.ReminderOffset < 0
                ) {
                    inboxReminder.ReminderOffset += duration + buffer;
                }
                return inboxReminder;
            });

            if (store.isCreate) {
                logReminderFollowUpTelemetry(
                    correctedInboxReminders,
                    meetingType?.ServiceId ?? '',
                    false
                );
            } else {
                logReminderFollowUpTelemetry(
                    correctedInboxReminders,
                    meetingType?.ServiceId ?? '',
                    true,
                    inboxRemindersGql
                );
            }
            perfDatapoint.end();
        } else {
            const faultMessage: string | undefined = result.MessageText;
            const responseCode = result.ResponseCode;
            const responseClass = result.ResponseClass;
            perfDatapoint.addCustomData({
                exceptionName: faultMessage,
                responseCode,
                responseClass,
            });
            perfDatapoint.endWithError(DatapointStatus.ServerError);
        }
    } else {
        perfDatapoint.endWithError(DatapointStatus.ServerError);
    }
    return result;
}
